import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Info } from "lucide-react";
import { PetitionCTA } from "@/components/PetitionCTA";
import { HERO_VARIANTS, IMAGES } from "@/lib/content";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: [0.33, 1, 0.68, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const lines = HERO_VARIANTS.A;
  const photoRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: photoRef, offset: ["start end", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const [showNote, setShowNote] = useState(false);

  return (
    <section data-testid="hero-section" className="relative overflow-hidden pt-28 md:pt-36">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 right-0 hidden h-[34rem] w-[34rem] translate-x-1/3 rounded-full bg-gooi-sand blur-3xl md:block"
      />
      <div className="relative mx-auto max-w-[90rem] px-5 md:px-12">
        <div className="grid items-start gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              data-testid="hero-overline"
              className="inline-flex items-center gap-2 rounded-full border border-gooi-green/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-gooi-forest"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-gooi-red" aria-hidden="true" />
              Samen voor 80 km/u · Meer rust langs de A1
            </motion.p>

            <h1
              data-testid="hero-heading"
              className="mt-6 font-display text-[2.6rem] font-medium leading-[1.05] tracking-tighter text-gooi-green sm:text-6xl lg:text-7xl xl:text-[5.25rem]"
            >
              {lines.map((line, i) => (
                <Line key={line} delay={0.25 + i * 0.18}>
                  {line}
                </Line>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-gooi-green/75 md:text-lg"
              data-testid="hero-subcopy"
            >
              80 km/u op de A1 tussen Naarden/Bussum en Eemnes geeft een geluidsenergiereductie van circa 37% — dat is theoretisch ongeveer 1 minuut extra reistijd over ±7 kilometer.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-9 flex flex-col items-start gap-5 sm:flex-row sm:items-center"
            >
              <PetitionCTA location="hero" size="xl" />
              <a
                href="#feiten"
                data-testid="hero-secondary-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-gooi-green/80 transition-colors duration-300 hover:text-gooi-green"
              >
                Bekijk waarom 80 km/u verschil maakt
                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" strokeWidth={1.5} aria-hidden="true" />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05 }}
              className="mt-14 grid grid-cols-1 gap-6 border-t border-gooi-green/15 pt-8 sm:grid-cols-3 sm:gap-8"
              data-testid="hero-stats"
            >
              <div>
                <p className="font-display text-3xl font-semibold tracking-tight text-gooi-green md:text-4xl">±7 km</p>
                <p className="mt-1 text-sm text-gooi-green/60">het traject</p>
              </div>
              <div>
                <p className="font-display text-3xl font-semibold tracking-tight text-gooi-green md:text-4xl">±1 minuut</p>
                <p className="mt-1 text-sm text-gooi-green/60">theoretisch extra reistijd</p>
              </div>
              <div className="relative">
                <p className="flex items-center gap-2 font-display text-3xl font-semibold tracking-tight text-gooi-green md:text-4xl">
                  ≈37%
                  <button
                    type="button"
                    data-testid="hero-db-info-button"
                    onClick={() => setShowNote((v) => !v)}
                    aria-expanded={showNote}
                    aria-label="Toelichting bij circa 37 procent minder geluidsenergie"
                    className="flex h-6 w-6 items-center justify-center rounded-full border border-gooi-green/25 text-gooi-green/60 transition-colors duration-300 hover:text-gooi-green"
                  >
                    <Info className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </p>
                <p className="mt-1 text-sm text-gooi-green/60">
                  minder geluidsenergie bij 2 dB reductie*
                </p>
                {showNote && (
                  <p
                    data-testid="hero-db-footnote"
                    className="absolute left-0 top-full z-10 mt-3 w-80 max-w-[calc(100vw-3rem)] rounded-xl border border-gooi-green/15 bg-white p-4 text-xs leading-relaxed text-gooi-green/80 shadow-lg"
                  >
                    * Een reductie van 2 dB komt door de logaritmische decibelschaal overeen met circa 37% minder
                    geluidsenergie. De werkelijke geluidsreductie op het A1-traject is afhankelijk van onder andere
                    verkeerssamenstelling, werkelijk gereden snelheid, wegdek, afstand en lokale omstandigheden.
                  </p>
                )}
              </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 1.2 }}
              className="mt-5 text-xs text-gooi-green/50"
              data-testid="hero-db-caption"
            >
              ≈ 2 dB reductie = ≈ 37% minder geluidsenergie
            </motion.p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.33, 1, 0.68, 1] }}
            className="hidden lg:col-span-5 lg:block"
          >
            <div ref={photoRef} className="photo-frame aspect-[3/4] shadow-2xl shadow-gooi-green/20">
              <motion.img
                src={IMAGES.hero}
                alt="Luchtfoto van de A1 door het groen bij Naarden/Bussum"
                loading="eager"
                style={{ y: photoY, scale: 1.18, willChange: "transform" }}
                className="duotone-photo h-full w-full object-cover"
              />
            </div>
            <p className="mt-3 text-right text-xs text-gooi-green/50">
              De A1 door het Gooi
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
